import { useCallback, useEffect, useState } from 'react';

import analyticsService from '../../../../services/analyticsService';

type FlaggedMessageCategory = {
  category: string;
  score: number;
};

type AnnotationStatus = 'PENDING' | 'REVIEWED' | 'DISMISSED';

type FlaggedMessage = {
  annotationId: string;
  annotationStatus: AnnotationStatus;
  annotationReviewedAt: string | null;
  annotationReviewedById: string | null;
  annotationNotes: string | null;
  messageId: string;
  conversationId: string;
  content: string;
  createdAt: string;
  studentName: string;
  studentLastName: string;
  studentExternalId: string;
  categories: FlaggedMessageCategory[];
  groupName?: string;
  reviewerName?: string;
};

type FlaggedMessagesResponse = {
  messages: FlaggedMessage[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
};

type UseInappropriateUseSectionProps = {
  allCourseIds: string[];
  classValue: string;
  groupCourseMap: Record<string, string[]>;
  timePeriodValue: string;
};

type UseInappropriateUseSectionReturn = {
  error: string | null;
  handleMarkAllAsAttended: () => void;
  loading: boolean;
  markingAll: boolean;
  messages: FlaggedMessage[];
  refetchMessages: () => void;
  removeMessage: (annotationId: string) => void;
  total: number;
};

function getDateRange(timePeriodValue: string): { startDate: string | undefined; endDate: string | undefined } {
  const now = new Date();
  const endDate = now.toISOString();
  let startDate: Date;

  switch (timePeriodValue) {
    case '7d':
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case '30d':
      startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      break;
    case '3m':
      startDate = new Date(now);
      startDate.setMonth(startDate.getMonth() - 3);
      break;
    default:
      return { startDate: undefined, endDate: undefined };
  }

  return { startDate: startDate.toISOString(), endDate };
}

function useInappropriateUseSection({
  allCourseIds,
  classValue,
  groupCourseMap,
  timePeriodValue,
}: UseInappropriateUseSectionProps): UseInappropriateUseSectionReturn {
  const [messages, setMessages] = useState<FlaggedMessage[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [markingAll, setMarkingAll] = useState<boolean>(false);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const courseIds = classValue === 'all' ? allCourseIds : groupCourseMap[classValue] || [];
      const { startDate, endDate } = getDateRange(timePeriodValue);

      const data: FlaggedMessagesResponse = await analyticsService.getFlaggedMessages({
        courseIds,
        startDate,
        endDate,
        limit: 3,
        offset: 0,
        statuses: 'PENDING',
      });

      setMessages(data.messages);
      setTotal(data.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setMessages([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [classValue, timePeriodValue, allCourseIds, groupCourseMap]);

  useEffect(() => {
    if (allCourseIds.length > 0) {
      fetchMessages();
    } else {
      setLoading(false);
      setMessages([]);
      setTotal(0);
    }
  }, [fetchMessages, allCourseIds.length]);

  const refetchMessages = useCallback(async () => {
    try {
      const courseIds = classValue === 'all' ? allCourseIds : groupCourseMap[classValue] || [];
      const { startDate, endDate } = getDateRange(timePeriodValue);

      const data: FlaggedMessagesResponse = await analyticsService.getFlaggedMessages({
        courseIds,
        startDate,
        endDate,
        limit: 3,
        offset: 0,
        statuses: 'PENDING',
      });

      setMessages(data.messages);
      setTotal(data.pagination.total);
    } catch {
      // Silent refetch — don't overwrite existing data on failure
    }
  }, [classValue, timePeriodValue, allCourseIds, groupCourseMap]);

  const removeMessage = useCallback((annotationId: string) => {
    setMessages((prev) => prev.filter((msg) => msg.annotationId !== annotationId));
    setTotal((prev) => Math.max(0, prev - 1));
  }, []);

  const handleMarkAllAsAttended = useCallback(async () => {
    const pendingMessages = messages.filter((msg) => msg.annotationStatus === 'PENDING');
    if (pendingMessages.length === 0) return;

    setMarkingAll(true);
    try {
      await Promise.allSettled(pendingMessages.map((msg) => analyticsService.updateAnnotationStatus(msg.annotationId, { status: 'REVIEWED' })));
      await fetchMessages();
    } finally {
      setMarkingAll(false);
    }
  }, [messages, fetchMessages]);

  return { error, handleMarkAllAsAttended, loading, markingAll, messages, refetchMessages, removeMessage, total };
}

export default useInappropriateUseSection;
export type { AnnotationStatus, FlaggedMessage, FlaggedMessageCategory };
